/**
 * UX Physics Layout - Persistent canvas with hot-swappable stages
 *
 * This layout provides a single BabylonJS canvas that persists across
 * child route changes. The StageManager handles hot-swapping assets
 * when navigating between /gem, /bob, and /rune.
 *
 * Key benefits:
 * - Inspector stays open across route changes
 * - No engine/scene recreation on navigation
 * - Shared physics infrastructure (Havok WASM loaded once)
 */

import type { Scene } from "@babylonjs/core";
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import type { StageConfig } from "@tony/nl-babylon";
import { BabylonCanvas, BabylonProvider } from "@tony/nl-babylon/react";
import { Plus, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { InspectorToggleButton } from "@/babylon/components/controls/InspectorToggleButton";
import { ViewerConfigPanel } from "@/babylon/components/controls/panels/ViewerConfigPanel";
import {
  StageProvider,
  useStage,
  useStageInitializer,
} from "@/babylon/context/StageContext";
import {
  useViewerRefs,
  ViewerRefsProvider,
} from "@/babylon/context/ViewerRefsContext";
import { registerAllStagePacks } from "@/babylon/stages";
import { JagexDisclaimer } from "@/components/legal/JagexDisclaimer";
import { Select, type SelectOption } from "@/components/ui/Select";
import { useDebug } from "@/context/DebugContext";
import { useViewer } from "@/context/ViewerContext";
import { cn } from "@/utils/cn";

export const Route = createFileRoute("/_immersive/demo/3d/ux")({
  component: UxPhysicsLayout,
});

/** Standalone tabs — rendered as Link-based tab buttons */
const STANDALONE_TABS = [
  { id: "gem", label: "Gem", to: "/demo/3d/ux/gem" },
  { id: "bob", label: "Bob", to: "/demo/3d/ux/bob" },
] as const;

/** Variant groups — each rendered as a Select dropdown */
const VARIANT_GROUPS = [
  {
    id: "rune",
    label: "Rune",
    options: [
      { group: "Elemental", label: "Air", value: "air" },
      { group: "Elemental", label: "Water", value: "water" },
      { group: "Elemental", label: "Earth", value: "earth" },
      { group: "Elemental", label: "Fire", value: "fire" },
      { group: "Catalytic", label: "Mind", value: "mind" },
      { group: "Catalytic", label: "Body", value: "body" },
      { group: "Catalytic", label: "Cosmic", value: "cosmic" },
      { group: "Catalytic", label: "Chaos", value: "chaos" },
      { group: "Catalytic", label: "Astral", value: "astral" },
      { group: "Catalytic", label: "Nature", value: "nature" },
      { group: "Catalytic", label: "Law", value: "law" },
      { group: "Catalytic", label: "Death", value: "death" },
      { group: "Catalytic", label: "Blood", value: "blood" },
      { group: "Catalytic", label: "Soul", value: "soul" },
      { group: "Catalytic", label: "Wrath", value: "wrath" },
      { group: "Combination", label: "Dust", value: "dust" },
      { group: "Combination", label: "Lava", value: "lava" },
      { group: "Combination", label: "Mist", value: "mist" },
      { group: "Combination", label: "Mud", value: "mud" },
      { group: "Combination", label: "Smoke", value: "smoke" },
      { group: "Combination", label: "Steam", value: "steam" },
    ] satisfies SelectOption[],
    to: "/demo/3d/ux/rune",
  },
  {
    id: "drill",
    label: "Drill",
    options: [
      { label: "Drill", value: "default" },
      { label: "Drill v6", value: "drill-006" },
      { label: "Drill Final", value: "drill-final" },
    ] satisfies SelectOption[],
    to: "/demo/3d/ux/drill",
  },
  {
    id: "ns",
    label: "NS",
    options: [
      { group: "Debug", label: "Crystal Debug", value: "crystal-debug" },
      { group: "Debug", label: "Spheroid v0.8", value: "spheroid-v0-8" },
      { group: "Beta", label: "Crystal β", value: "crystal-beta" },
      { group: "Beta", label: "Flat β", value: "flat-beta" },
      { group: "Beta", label: "Spherical β", value: "spherical-beta" },
      { group: "Final", label: "Crystal", value: "crystal-final" },
      { group: "Final", label: "Flat", value: "flat-final" },
      { group: "Final", label: "Spherical", value: "spherical-final" },
      { group: "v0.7", label: "Crystal v0.7", value: "crystal-v0-7" },
      {
        group: "v0.7",
        label: "Crystal v0.7 FC",
        value: "crystal-v0-7-compressed",
      },
      { group: "v0.7", label: "Spherical v0.7", value: "spherical-v0-7" },
      {
        group: "v0.7",
        label: "Spherical v0.7 FC",
        value: "spherical-v0-7-compressed",
      },
      {
        group: "Compressed",
        label: "Crystal FC",
        value: "crystal-final-compressed",
      },
      {
        group: "Compressed",
        label: "Flat FC",
        value: "flat-final-compressed",
      },
      {
        group: "Compressed",
        label: "Spherical FC",
        value: "spherical-final-compressed",
      },
    ] satisfies SelectOption[],
    to: "/demo/3d/ux/ns",
  },
] as const;

// Register all stage packs at module load time
registerAllStagePacks();

function UxPhysicsLayout() {
  return (
    <StageProvider>
      <BabylonProvider>
        <ViewerRefsProvider>
          <UxPhysicsLayoutInner />
        </ViewerRefsProvider>
      </BabylonProvider>
    </StageProvider>
  );
}

function UxPhysicsLayoutInner() {
  const { isDebugEnabled } = useDebug();
  const { viewerType } = useViewer();
  const { initializeManagers } = useStageInitializer();
  const { isLoading, loadStage, resetStage, isInitialized } = useStage();
  const { threeRefs, modelViewerElement } = useViewerRefs();
  const [spinningTab, setSpinningTab] = useState<string | null>(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { glb?: string };

  // Show Jagex disclaimer only on RuneScape content pages
  const isJagexContent =
    pathname.includes("/bob") || pathname.includes("/rune");

  // Hide Babylon canvas when a non-Babylon viewer is active
  const shouldHideBabylon = viewerType !== "babylon";

  const handleSceneReady = useCallback(
    async (scene: Scene) => {
      console.log("[UxLayout] SCENE_READY - initializing managers");
      await initializeManagers(scene);
    },
    [initializeManagers],
  );

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      {/* Tab Navigation */}
      <nav className="shrink-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-2 py-1">
        <div className="flex gap-1 items-center">
          {/* Back button */}
          <Link
            className={cn(
              "px-2 py-1.5 text-xs font-medium rounded-md",
              "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800",
            )}
            to=".."
          >
            ← Back
          </Link>
          <span className="text-gray-300 dark:text-gray-600 mx-1">|</span>

          {/* Standalone tabs */}
          {STANDALONE_TABS.map((tab) => (
            <Link className="flex items-center" key={tab.id} to={tab.to}>
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      "px-3 py-1.5 text-xs font-medium rounded-l-md",
                      isActive
                        ? "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/80 dark:hover:bg-gray-700/80 active:bg-gray-300 dark:active:bg-gray-600 active:scale-[0.98]",
                    )}
                  >
                    {tab.label}
                  </span>
                  <button
                    className={cn(
                      "h-7 px-1.5 text-xs font-medium rounded-r-md border-l border-gray-200 dark:border-gray-700",
                      "bg-gray-100 dark:bg-gray-800 select-none cursor-pointer flex items-center justify-center",
                      isActive
                        ? "text-theme-primary hover:text-theme-primary/80 hover:bg-gray-200/80 dark:hover:bg-gray-700/80 active:scale-[0.98]"
                        : "text-gray-400 hover:text-theme-primary hover:bg-gray-200/80 dark:hover:bg-gray-700/80 active:scale-[0.98]",
                    )}
                    disabled={!isInitialized}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isActive) {
                        setSpinningTab(tab.id);
                        resetStage(tab.id);
                        setTimeout(() => setSpinningTab(null), 400);
                      } else {
                        loadStage(tab.id, "insert");
                      }
                    }}
                    title={
                      isActive
                        ? `Reset ${tab.label} scene`
                        : `Add more ${tab.label} to scene`
                    }
                    type="button"
                  >
                    {isActive ? (
                      <RotateCcw
                        className={cn(
                          "size-3.5",
                          spinningTab === tab.id &&
                            "animate-[spin-ccw_0.4s_ease-out]",
                        )}
                      />
                    ) : (
                      <Plus className="size-3.5" />
                    )}
                  </button>
                </>
              )}
            </Link>
          ))}

          {/* Variant group dropdowns */}
          {VARIANT_GROUPS.map((group) => {
            const isGroupActive = pathname.startsWith(group.to);
            const currentGlb = isGroupActive ? search.glb : undefined;

            return (
              <div className="flex items-center" key={group.id}>
                <Select
                  className="w-32"
                  onValueChange={(value) => {
                    navigate({
                      search: { glb: value },
                      to: group.to,
                    });
                  }}
                  options={group.options as unknown as SelectOption[]}
                  placeholder={group.label}
                  position="popper"
                  size="compact"
                  value={currentGlb}
                  variant="demo"
                />
                {isGroupActive && (
                  <button
                    className={cn(
                      "h-7 px-1.5 ml-0.5 text-xs font-medium rounded-md",
                      "bg-gray-100 dark:bg-gray-800 select-none cursor-pointer flex items-center justify-center",
                      "text-theme-primary hover:text-theme-primary/80 hover:bg-gray-200/80 dark:hover:bg-gray-700/80 active:scale-[0.98]",
                    )}
                    disabled={!isInitialized}
                    onClick={() => {
                      setSpinningTab(group.id);
                      resetStage(group.id);
                      setTimeout(() => setSpinningTab(null), 400);
                    }}
                    title={`Reset ${group.label} scene`}
                    type="button"
                  >
                    <RotateCcw
                      className={cn(
                        "size-3.5",
                        spinningTab === group.id &&
                          "animate-[spin-ccw_0.4s_ease-out]",
                      )}
                    />
                  </button>
                )}
              </div>
            );
          })}

          {isLoading && (
            <span className="ml-2 text-xs text-gray-500">Loading...</span>
          )}
          <div className="ml-auto">
            <InspectorToggleButton />
          </div>
        </div>
      </nav>

      {/* Canvas Container */}
      <div className="flex-1 min-h-0 overflow-hidden relative">
        {/* Wrapper div isolates Inspector v2 DOM from React's tree.
            Inspector appends #babylon-inspector-container to canvas.parentElement,
            so this div absorbs those out-of-band DOM mutations. */}
        <div className={cn("w-full h-full", shouldHideBabylon && "hidden")}>
          <BabylonCanvas
            className="block w-full h-full"
            debugMode={isDebugEnabled && !shouldHideBabylon}
            onSceneReady={handleSceneReady}
            sceneId="ux-physics"
            suspended={shouldHideBabylon}
          />
        </div>

        {/* Outlet for child routes - they call loadStage() on mount */}
        <div
          className={cn(
            "absolute inset-0",
            !shouldHideBabylon && "pointer-events-none",
          )}
        >
          <Outlet />
        </div>

        {/* Config panel for Three.js / model-viewer (Babylon has its own Inspector) */}
        {shouldHideBabylon && (
          <ViewerConfigPanel
            isOpen={isDebugEnabled}
            modelViewerElement={modelViewerElement}
            threeRefs={threeRefs}
            viewerType={viewerType}
          />
        )}

        {/* Jagex trademark disclaimer for RuneScape content */}
        {isJagexContent && <JagexDisclaimer />}
      </div>
    </div>
  );
}

/**
 * Hook for child routes to load their stage on mount
 * @param stageId - The stage pack ID to load
 * @param configOverrides - Optional partial config (e.g., glbKey from route query params)
 * @param enabled - Whether to actually load the stage (default true). Set false
 *   when a non-Babylon viewer is active so the hook can still be called
 *   unconditionally (React rules of hooks).
 */
export function useLoadStageOnMount(
  stageId: string,
  configOverrides?: Partial<StageConfig>,
  enabled = true,
) {
  const { loadStage, isInitialized } = useStage();

  useEffect(() => {
    if (enabled && isInitialized) {
      loadStage(stageId, "open", configOverrides);
    }
  }, [enabled, isInitialized, loadStage, stageId, configOverrides]);
}
